import { Dispatch, ReactNode, SetStateAction, createContext, useContext, useEffect, useState } from "react"
import componentApi from "../../../services/api/component"

interface ComponentBriefinfContextProviderProps {
    children: ReactNode
}

export interface ComponentFile {
    componente_id: number
    id: number,
    referencia: string
    nome: string
}

export interface Component {
    id: number
    briefing_id: number
    posicao: number
    conteudo?: string
    tipoComponente: 'texto' | 'titulo' | 'img' | 'pdf' | 'audio'
    componentImgs?: ComponentFile[]
}

interface UpdateComponent {
    id: number,
    position: number
}

const initialState: Component[] = []

interface ComponentContext {
    idComponent?: number,
    componentContent: string
    file?: File[],
    listFile: ComponentFile[],
    loadingImages: boolean,
    setIdComponent: Dispatch<SetStateAction<number | undefined>>,
    setComponentContent: Dispatch<SetStateAction<string>>,
    setFile: Dispatch<SetStateAction<File[]>>,
    setListFile: Dispatch<SetStateAction<ComponentFile[]>>

    components: Component[]

    fetchComponents: (idBriefing: number) => void
    addComponent: (idClient: number, idBriefing: number, idAttendance?: number, conteudo?: string, tipoComponente?: string, file?: File[]) => void
    deleteComponent: (idBriefing: number, id: number) => void
    updateComponent: (idClient: number, idBriefing: number, idComponent: number, idAttendance?: number, content?: string, file?: File[]) => void
    updatePosition: (updateComponent: UpdateComponent[], idBriefing: number) => void

    getFile: (idComponent: number) => void
    uploadFile: (file?: File[], idClient?: number, idComponent?: number, idAttendance?: number) => void
    deleteFile: (idBriefing: number, idImage: number) => void
}

export const ComponentContext = createContext({} as ComponentContext)

export const useComponentContext = () => useContext(ComponentContext)

export function ComponentBriefingContext({ children }: ComponentBriefinfContextProviderProps) {
    const [components, setComponents] = useState<Component[]>(initialState)
    const [file, setFile] = useState<File[]>([])
    const [componentContent, setComponentContent] = useState('')
    const [idComponent, setIdComponent] = useState<number>()
    const [listFile, setListFile] = useState<ComponentFile[]>([])
    const [loadingImages, setLoadingImages] = useState(false)

    /*
        Lista os componentes referente
        com o id_briefing
    */
    async function fetchComponents(idBriefing: number) {
        try {
            const response = await componentApi.listByBriefingId(idBriefing);
            const componentsData = response.data.list.componentes
            setComponents(componentsData)

            for (const item of componentsData) {
                await getFile(item.id)
            }
        } catch (error) {
            alert(`Failed to fetch components: ${error}`)
        }
    }

    /*
        Adiciona o componente de texto e/ou imagem
        referente ao id_briefing
    */
    async function addComponent(idClient: number, idBriefing: number, idAttendance?: number, conteudo?: string, tipoComponente?: string, image?: File[]) {
        try {
            if (conteudo || (image && image.length !== 0)) {
                const response = await componentApi.createComponent(idBriefing, {
                    conteudo, tipoComponente
                })

                await uploadFile(image, idClient, response.data.id, idAttendance)
                await fetchComponents(idBriefing)

                resetStates()
            }
        } catch (err) {
            alert(err)
        }
    }

    /*
        Deleta componente de texto e/ou 
        imagem referente ao um id_component
    */
    async function deleteComponent(idBriefing: number, idComponent: number) {
        try {
            await componentApi.deleteComponent(idComponent);
            await fetchComponents(idBriefing)
        } catch (error) {
            alert(`Failed to delete component: ${error}`)
        }
    }

    /*
      Altera componente de texto e/ou imagem, 
      permitindo deletar imagens, inserir novas e alterar o texto
  */
    async function updateComponent(idClient: number, idBriefing: number, idComponent: number, idAttendance?: number, content?: string, image?: File[]) {
        try {
            if (content) {
                // await api.patch(`component/updateComponent/${idComponent}`, { content })
                await componentApi.updateComponent(idComponent, { content })
            }

            await uploadFile(image, idClient, idComponent, idAttendance)
            await fetchComponents(idBriefing)

            resetStates()
        } catch (err) {
            alert(err)
        }
    }

    /*
        Altera posição do componente
    */
    async function updatePosition(components: UpdateComponent[], idBriefing: number) {
        const updates = components.map(component => ({
            id: component.id,
            position: component.position
        }));

        try {
            await componentApi.updatePositionComponent(updates);
            await fetchComponents(idBriefing)
        } catch (error) {
            console.error('Error updating positions:', error);
        }
    }


    /*
        Envia imagens para um determinado compoente
        referente a id_coponent
    */
    async function uploadFile(file?: File[], idClient?: number, idComponent?: number, idAttendance?: number) {
        const formData = new FormData()

        if (!file || file.length === 0) return

        file?.forEach(file =>
            formData.append('image', file)
        )

        try {
            if (!idComponent || !idClient || !idAttendance) throw new Error('Missing parameters')

            await componentApi.uploadFile(idComponent, idClient, idAttendance, formData)
        } catch (error) {
            alert(`Failed to send image: ${error}`)
        }
    }

    /*
        Lista imagens relacionadas 
        a com id_component
    */
    async function getFile(idComponent: number) {
        try {
            setLoadingImages(true)

            // const response = await api.get(`/componentImage/listComponentImage/${idComponent}`)
            const response = await componentApi.listComponentFileByComponentId(idComponent)
            const files = response.data.componenteArquivo.map((file: ComponentFile) => ({ ...file, component_id: idComponent }))


            setListFile(prevListFile => [
                ...prevListFile.filter(file => file.componente_id !== idComponent),
                ...files
            ])
        } catch (err) {
            alert(err)
        } finally {
            setLoadingImages(false)
        }
    }

    /*
        Deleta iamgem referente ao idComponent e
        idImage
    */
    async function deleteFile(idBriefing: number, idImage: number) {
        try {
            await componentApi.deleteFile(idImage)

            fetchComponents(idBriefing)
        } catch (err) {
            alert(err)
        }
    }

    useEffect(() => {
        components.forEach(item => {
            getFile(item.id)
        });
    }, [components])

    function resetStates() {
        setComponentContent('')
        setFile([])
        setIdComponent(undefined)
    }

    return (
        <ComponentContext.Provider
            value={{
                idComponent,
                setIdComponent,
                componentContent,
                setComponentContent,
                file,
                setFile,
                listFile,
                setListFile,
                components,
                loadingImages,
                addComponent,
                deleteComponent,
                fetchComponents,
                uploadFile,
                getFile,
                updateComponent,
                updatePosition,
                deleteFile

            }}
        >
            {children}
        </ComponentContext.Provider>
    )
}